import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

/**
 * CSRF token plumbing for the SPA — VULN-013.
 *
 * The backend holds the CSRF token server-side in the session (a synchronizer token) and publishes
 * the current value in an `X-CSRF-TOKEN` response header on every response — there is no CSRF
 * cookie. We capture that value from each response and echo it back as the `X-XSRF-TOKEN` request
 * header on state-changing requests, which is what the server validates against the token stored in
 * the session. A cross-site attacker cannot read response headers, so they cannot learn the token.
 *
 * A cookie-based token was tried first and abandoned: the cookie is scoped to the servlet context
 * path (`/api`) and so was never returned on the paths the SPA calls, Tableau sets its own
 * `XSRF-TOKEN` at path `/` on the same domain, and an edge proxy force-adds `HttpOnly` — making the
 * cookie unreadable to JavaScript regardless. None of those apply when there is no cookie.
 *
 * The token is captured from error responses too, so a request rejected because the session (and
 * with it the token) was lost can succeed on retry.
 */

const CSRF_RESPONSE_HEADER = "x-csrf-token"; // axios lowercases response header keys
const CSRF_REQUEST_HEADER = "X-XSRF-TOKEN"; // header name Spring Security expects
const MUTATING_METHODS = ["post", "put", "patch", "delete"];

let csrfToken: string | undefined;

/** The current CSRF token, as captured from the most recent response that carried one. */
export function getCsrfToken(): string | undefined {
	return csrfToken;
}

function captureFromHeaders(headers: unknown): void {
	const token = (headers as Record<string, string> | undefined)?.[CSRF_RESPONSE_HEADER];
	if (typeof token === "string" && token.length > 0) {
		csrfToken = token;
	}
}

let installed = false;

/**
 * Registers global axios interceptors: capture the token from every response's `X-CSRF-TOKEN`
 * header, and attach it as `X-XSRF-TOKEN` (plus credentials) to every state-changing request.
 * Idempotent — safe to call more than once.
 */
export function installCsrfInterceptors(): void {
	if (installed) {
		return;
	}
	installed = true;

	axios.interceptors.response.use(
		(response: AxiosResponse) => {
			captureFromHeaders(response.headers);
			return response;
		},
		(error) => {
			// Error responses (e.g. 401/403) also carry the header; capture it so a retry can succeed.
			captureFromHeaders(error?.response?.headers);
			return Promise.reject(error);
		}
	);

	axios.interceptors.request.use((config: AxiosRequestConfig) => {
		config.withCredentials = true;
		const method = (config.method ?? "get").toLowerCase();
		if (MUTATING_METHODS.indexOf(method) !== -1) {
			const token = getCsrfToken();
			if (token) {
				config.headers = { ...(config.headers ?? {}), [CSRF_REQUEST_HEADER]: token };
			}
		}
		return config;
	});
}
